import { Alert, Button, Col, DatePicker, Form, Row, Select, Space, Spin, Typography } from "antd";
import dayjs from "dayjs";
import { rangoDeHoy, rangoDeMeses } from "./atajosDeFecha";
import { useEffect, useState } from "react";
import { getAllCajas } from "../../services/cajaService";
import { getAllTurnos } from "../../services/turnoService";
import { getAllTPVs } from "../../services/tpvService";
import {
  getAperturaActiva,
  getCajaHistorico,
  setCajaHistorico,
} from "../../services/cajaEstadoService";
import { useSyncHistoricos } from "../../hooks/useSyncHistoricos";
import { useAuth } from "../../contexts/AuthContext";
import { filtrarTiendasPorUsuario } from "../../utils/cajasDelUsuario";
import db from "../../db/database";
import "../CierreDeCaja/CierreDeCajaStyles.css";

const { Option } = Select;

const TraerHistoricosForm = ({
  handleCancel,
  handleHistoricosSynced,
  onRefreshDashboard,
  country,
}) => {
  const [form] = Form.useForm();
  const [turnos, setTurnos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [tpvs, setTPVs] = useState([]);
  const [aperturaActiva, setAperturaActiva] = useState(null);
  const [cajaHistorico, setCajaHistoricoGuard] = useState(null);
  const selectedTPV = Form.useWatch("TPV", form);
  const cajaBloqueada = !!aperturaActiva;
  const { currentUser } = useAuth();

  const { loading, setLoading, error, success, setError, setSuccess, handleSubmit } =
    useSyncHistoricos(handleHistoricosSynced, country);

  const handleFormSubmit = async (values) => {
    const ok = await handleSubmit(values);
    if (ok) {
      setCajaHistorico(values.Caja, {
        FechaInicio: dayjs(values.FechaInicio).format("YYYY-MM-DD"),
        FechaFin: dayjs(values.FechaFin).format("YYYY-MM-DD"),
      });
      setCajaHistoricoGuard({ Caja: values.Caja, timestamp: new Date().toISOString() });
    }
    onRefreshDashboard?.();
  };

  const handleCajaSelectChange = (value) => {
    if (!cajaHistorico || value === cajaHistorico.Caja) {
      form.setFieldValue("Caja", value);
      return;
    }

    // revertir visualmente hasta que el usuario confirme
    form.setFieldValue("Caja", cajaHistorico.Caja);

    window.messageApi.confirm({
      title: "Cambio de caja",
      content: (
        <>
          <p>
            Ya trajo histórico de la caja {cajaHistorico.Caja}. Si continúa, se eliminarán esos
            datos para traer los de la caja {value}.
          </p>
          <p>¿Desea continuar?</p>
        </>
      ),
      okText: "Sí, cambiar",
      cancelText: "No",
      onOk: async () => {
        await db.invoicePaymentLines.clear();
        await db.invoiceLines.clear();
        await db.invoices.clear();

        form.setFieldValue("Caja", value);
        setCajaHistoricoGuard({ Caja: value, timestamp: new Date().toISOString() });
      },
      onCancel: () => {
        form.setFieldValue("Caja", cajaHistorico.Caja);
      },
    });
  };

  // Presets de rango: fecha fin = hoy, fecha inicio = hoy menos N meses.
  const aplicarRango = (rango) => {
    form.setFieldsValue(rango);
    form.validateFields(["FechaInicio", "FechaFin"]).catch(() => {});
  };

  const applyDateRangePreset = (months) => aplicarRango(rangoDeMeses(months));
  const aplicarHoy = () => aplicarRango(rangoDeHoy());

  useEffect(() => {
    // reset Caja cuando Tienda cambia (salvo que esté fija por caja abierta)
    if (cajaBloqueada) return;
    form.setFieldsValue({ Caja: undefined });
  }, [selectedTPV, cajaBloqueada, form]);

  useEffect(() => {
    const cargarEstadoCaja = async () => {
      const apertura = await getAperturaActiva();
      if (apertura) {
        setAperturaActiva(apertura);
        form.setFieldsValue({ TPV: apertura.TPV, Caja: apertura.Caja });
      } else {
        setCajaHistoricoGuard(getCajaHistorico());
      }
    };
    cargarEstadoCaja();
  }, [form]);

  useEffect(() => {
    setLoading(true);
    const cargarDatos = async () => {
      try {
        const cajasData = await getAllCajas();
        setCajas(cajasData);

        const turnosData = await getAllTurnos();
        setTurnos(turnosData);

        const tpvList = await getAllTPVs();
        setTPVs(filtrarTiendasPorUsuario(tpvList, currentUser));
      } catch (error) {
        console.error("Error al cargar datos:", error);
        setError("Error al cargar datos de turnos y cajas");
      } finally {
        setLoading(false);
      }
    };
    cargarDatos();
  }, [currentUser, setError, setLoading]);

  return (
    <div>
      <Typography.Title level={4}>Sincronizar datos</Typography.Title>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError("")}
        />
      )}
      {success && (
        <Alert
          message="Sincronización exitosa"
          description={<div style={{ whiteSpace: "pre-line" }}>{success}</div>}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setSuccess("")}
        />
      )}

      <Spin spinning={loading} tip="Cargando...">
        <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
          <div className="cierreDeCaja-header-section">
            <h3>Información general</h3>
            <Row gutter={16}>
              <p>
                ⚠️ Verifique que no existan facturas en estado "Pendiente" de sincronizar. Una vez
                aplicados y confirmados los filtros, estos no podrán recuperarse.
              </p>
            </Row>

            {cajaBloqueada && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`La caja está fija en ${aperturaActiva.Caja} porque ya existe una caja abierta. Cierre la caja actual para traer histórico de otra caja.`}
              />
            )}

            <div className="multi-currency-section">
              <h3>Filtros</h3>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="TPV"
                    label="Tienda"
                    rules={[{ required: true, message: "Seleccione una tienda" }]}
                  >
                    <Select
                      placeholder="Seleccione una tienda"
                      loading={loading}
                      allowClear
                      disabled={cajaBloqueada}
                      notFoundContent={
                        loading ? null : "No hay tiendas disponibles para su usuario"
                      }
                      onChange={() => {
                        // reset Caja cuando Tienda cambia
                        form.setFieldsValue({ Caja: undefined });
                      }}
                    >
                      {tpvs.map((tpv) => (
                        <Option key={tpv.No} value={tpv.No}>
                          {tpv.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="Caja"
                    label="Caja"
                    rules={[{ required: true, message: "Seleccione la caja" }]}
                    shouldUpdate={(prev, curr) => prev.TPV !== curr.TPV}
                  >
                    <Select
                      placeholder="Seleccione una caja"
                      loading={loading}
                      allowClear
                      optionLabelProp="label"
                      disabled={!selectedTPV || cajaBloqueada}
                      onChange={handleCajaSelectChange}
                    >
                      {cajas
                        .filter((caja) => caja.TPV === selectedTPV)
                        .map((caja) => (
                          <Option
                            key={caja.No}
                            value={caja.No}
                            label={`${caja.No} - ${caja.nombre}`}
                          >
                            {`${caja.No} - ${caja.nombre}`}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="FechaInicio"
                    label="Fecha inicio"
                    rules={[{ required: true, message: "Seleccione la fecha inicio" }]}
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="FechaFin"
                    label="Fecha fin"
                    dependencies={["FechaInicio"]}
                    rules={[
                      { required: true, message: "Seleccione la fecha fin" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const fechaInicio = getFieldValue("FechaInicio");
                          if (!fechaInicio) {
                            return Promise.reject(
                              new Error("Debe seleccionar la fecha inicio primero")
                            );
                          }
                          if (value && value.isAfter(dayjs(), "day")) {
                            return Promise.reject(new Error("La fecha fin no puede ser futura"));
                          }
                          if (value && value.isAfter(dayjs(fechaInicio).add(6, "month"), "day")) {
                            return Promise.reject(
                              new Error(
                                "La fecha fin no puede ser más de 6 meses después de la fecha inicio"
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <DatePicker
                      format="DD/MM/YYYY"
                      style={{ width: "100%" }}
                      disabledDate={(current) => {
                        const fechaInicio = form.getFieldValue("FechaInicio");
                        if (!fechaInicio) {
                          return true; // Deshabilitar todo hasta que elija inicio
                        }
                        return (
                          current < dayjs(fechaInicio).startOf("day") || // antes de inicio
                          current > dayjs().endOf("day") || // después de hoy
                          current > dayjs(fechaInicio).add(6, "month").endOf("day") // max de 6 meses
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Space wrap style={{ marginBottom: 16 }}>
                    <Button size="small" onClick={aplicarHoy}>
                      Hoy
                    </Button>
                    <Button size="small" onClick={() => applyDateRangePreset(1)}>
                      1 mes
                    </Button>
                    <Button size="small" onClick={() => applyDateRangePreset(3)}>
                      3 meses
                    </Button>
                    <Button size="small" onClick={() => applyDateRangePreset(6)}>
                      6 meses
                    </Button>
                  </Space>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="Turno" label="Turno">
                    <Select placeholder="Seleccione un turno" loading={loading} allowClear>
                      {turnos.map((turno) => (
                        <Option key={turno.No} value={turno.No}>
                          {turno.DescripcionTurno}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>
          </div>

          <div className="cierreDeCaja-form-actions">
            <Button onClick={handleCancel}>Cancelar</Button>
            <Button type="primary" htmlType="submit">
              Aceptar
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default TraerHistoricosForm;
